import React, { Component } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { withApollo } from 'react-apollo';
import { Alert, Button, Col, Glyphicon, Modal, Row } from 'react-bootstrap';
import { branch, compose, renderNothing, withProps } from 'recompose';


import BaseInput from '../../../fields/BaseInput';
import WellDropdownListInput from '../../../fields/WellDropdownListInput';
import TicketCompactList from '../../../tickets/components/TicketCompactList';


import { wellMergeApi } from '../graphql/wellMergeApi';
import wellTicketsApi from '../graphql/wellTicketsApi';
import { tryHandleGraphQLError } from '../../../../utils/graphQLErrorHandlers';


const propTypes = exact({
  // eslint-disable-next-line react/no-unused-prop-types
  wellLookup: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,

  wellId: PropTypes.number.isRequired,
  wellName: PropTypes.string.isRequired,
  leaseName: PropTypes.string.isRequired,
  operatorName: PropTypes.string.isRequired,

  leaseId: PropTypes.number.isRequired,

  client: PropTypes.any.isRequired,
  tickets: PropTypes.array.isRequired,
});


class WellMergeModal extends Component {
  static getDefaultState() {
    return {
      wellId: null,
      error: null,
      submitting: false,
    };
  }

  constructor(props) {
    super(props);
    this.state = WellMergeModal.getDefaultState();

    this.onHide = this.onHide.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onWellChange = this.onWellChange.bind(this);
  }

  onHide() {
    const { onHide } = this.props;
    this.setState(WellMergeModal.getDefaultState());
    onHide();
  }

  async onSubmit() {
    const { client, wellId: sourceWellId, onSubmit } = this.props;
    const { wellId: targetWellId } = this.state;

    try {
      this.setState({ submitting: true, error: null });
      await wellMergeApi(client, sourceWellId, targetWellId);
      this.setState(WellMergeModal.getDefaultState());
      onSubmit();
    } catch (e) {
      const error = tryHandleGraphQLError(e);
      this.setState({ error });
    } finally {
      this.setState({ submitting: false });
    }
  }

  onWellChange(value) {
    const { id: wellId } = value;
    this.setState({ wellId });
  }

  isValid() {
    const { wellId } = this.state;
    const { tickets } = this.props;

    if (tickets.length > 20) {
      return false;
    }

    if (wellId === null) {
      return false;
    }

    return true;
  }

  isSubmitting() {
    return this.state.submitting;
  }

  renderError() {
    const { error } = this.state;
    if (error === null) {
      return null;
    }

    return (
      <Alert bsStyle="danger">
        <Glyphicon glyph="remove-circle" />
        {' '}
        <span>{error}</span>
      </Alert>
    );
  }

  renderOverwriteWarning() {
    const { tickets } = this.props;

    if (tickets.length === 0) {
      return null;
    } if (tickets.length <= 20) {
      return (
        <Alert bsStyle="warning">
          <Glyphicon glyph="warning-sign" />
          {' '}
          <span>Previous ticket facts will be overwritten as a result of this operation.</span>
          <TicketCompactList tickets={tickets} />
        </Alert>
      );
    }
    return (
      <Alert bsStyle="danger">
        <Glyphicon glyph="remove-circle" />
        {' '}
        <span>Can not merge more than 20 tickets.</span>
        <TicketCompactList tickets={tickets} />
      </Alert>
    );
  }

  render() {
    const { show } = this.props;
    const { wellName, leaseName, operatorName, leaseId } = this.props;
    const { wellId } = this.state;

    const disableSubmitButton = !this.isValid() || this.isSubmitting();

    return (
      <Modal show={show} onHide={this.onHide}>
        <Modal.Header closeButton>
          <Modal.Title>Select Destination Well</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderError()}
          {this.renderOverwriteWarning()}
          <p>
            Wells can only be merged on the same lease.
          </p>
          <div className="panel panel-default">
            <div className="panel-heading"><strong>Source</strong></div>
            <div className="panel-body">
              <Row>
                <Col xs={4}>
                  <BaseInput
                    componentProps={{ defaultValue: operatorName, disabled: true }}
                    componentClass="input"
                    label="Operator"
                  />
                </Col>
                <Col xs={4}>
                  <BaseInput
                    componentProps={{ defaultValue: leaseName, disabled: true }}
                    componentClass="input"
                    label="Lease"
                  />
                </Col>
                <Col xs={4}>
                  <BaseInput
                    componentProps={{ defaultValue: wellName, disabled: true }}
                    componentClass="input"
                    label="Well"
                  />
                </Col>
              </Row>
            </div>
          </div>
          <div className="panel panel-default">
            <div className="panel-heading"><strong>Destination</strong></div>
            <div className="panel-body">
              <form>
                <Row>
                  <Col xs={4}>
                    <BaseInput
                      componentProps={{ defaultValue: operatorName, disabled: true }}
                      componentClass="input"
                      label="Operator"
                    />
                  </Col>
                  <Col xs={4}>
                    <BaseInput
                      componentProps={{ defaultValue: leaseName, disabled: true }}
                      componentClass="input"
                      label="Lease"
                    />
                  </Col>
                  <Col xs={4}>
                    <WellDropdownListInput
                      input={{
                        onChange: this.onWellChange,
                        value: wellId,
                      }}
                      label="Well"
                      leaseId={leaseId}
                    />
                  </Col>
                </Row>
              </form>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            bsStyle="warning"
            onClick={this.onHide}
          >
            Cancel
          </Button>
          <Button
            disabled={disableSubmitButton}
            onClick={this.onSubmit}
            bsStyle="danger"
          >
            Merge
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}


WellMergeModal.propTypes = propTypes;


const withHide = branch(
  (props) => { return !props.wellLookup || !props.show; },
  renderNothing,
);


const withSpreadWell = withProps((props) => {
  const { well } = props.wellLookup;
  const { name: wellName, id: wellId, lease } = well;
  const { name: leaseName, id: leaseId, operator } = lease;
  const { name: operatorName } = operator;

  return {
    wellId,
    wellName,
    leaseId,
    leaseName,
    operatorName,
  };
});


const enhance = compose(
  withHide,
  withSpreadWell,
  wellTicketsApi,
  withApollo,
);


export default enhance(WellMergeModal);
